import React from 'react'

import { AdjustPaymentTarget } from '../AdjustPaymentModal'
import DiscrepancyRow from './DiscrepancyRow'
import {
  DISCREPANCY_COLUMNS,
  DISCREPANCY_COPY,
  DISCREPANCY_FILTER_OPTIONS,
  DISCREPANCY_GRID_TEMPLATE,
  DiscrepancyFilter,
} from './helpers'
import { discrepanciesOf, discrepancyKey } from './row-targets'
import QueuePanel from './QueuePanel'
import { DISCREPANCY_URL_CONTRACT } from './url-state'
import { useReviewQueuePanel } from './useReviewQueuePanel'
import { useRowDeepLink } from './useRowDeepLink'

/**
 * The "Discrepancies" tab — the open overpaid / underpaid / manual-review
 * benefit-months.
 *
 * The label names exactly this set and not a wider one (CONTEXT.md); nothing
 * that is not a discrepancy belongs on this tab. It read "Flagged issues" for
 * one round of EMS-10248, until that name was given to the `manual_review`
 * issue type — ONE of the three types listed here, so it could not also stand
 * for all of them.
 *
 * Note the deliberate mismatch: the REQUEST asks for `kind: 'discrepancy'` (the
 * operator-facing word) while the ROW discriminator stays at its original wire
 * value `'issue'`. Renaming either is breaking on the EPS side; do not conflate
 * them.
 *
 * An inbound `?tab=flagged&rqOpen=<benefitId>:<period>:<issueType>` opens one
 * row's modal straight away — see ./useRowDeepLink.
 */
const DiscrepanciesPanel: React.FC<{
  onOpenTarget: (target: AdjustPaymentTarget) => void
  canWrite: boolean
}> = ({ onOpenTarget, canWrite }) => {
  const panel = useReviewQueuePanel<DiscrepancyFilter>(DISCREPANCY_URL_CONTRACT)
  useRowDeepLink(panel, onOpenTarget)

  return (
    <QueuePanel
      panel={panel}
      copy={DISCREPANCY_COPY}
      columns={DISCREPANCY_COLUMNS}
      gridTemplateColumns={DISCREPANCY_GRID_TEMPLATE}
      filterOptions={DISCREPANCY_FILTER_OPTIONS}
      rows={discrepanciesOf(panel.items).map((issue) => (
        <DiscrepancyRow
          key={discrepancyKey(issue)}
          issue={issue}
          canWrite={canWrite}
          onOpenTarget={onOpenTarget}
        />
      ))}
    />
  )
}

export default DiscrepanciesPanel
